import { Bill } from '@enview/interface/types/bills/Bill';
import {
  faChartLine as TrackingIcon,
  faCheck as MarkAsReadIcon,
  faChevronRight as ChevronRightIcon,
  faFlag as PriorityIcon,
  faListCheck as WorkflowStatusIcon,
  faPencilAlt as PencilIcon,
  faTag as TagIcon,
  faUserPlus as AssigneeIcon,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import NestedDropdown from 'react-multilevel-dropdown';
import ArrowDown from '../../../components/svg/ArrowDown';
import BillTagContent from '../BillTagging/BillTagContent';
import BulkTrackingContent from '../BillTracking/BulkTrackingContent';
import BulkWorkflowStatusContent from '../BillTriage/BulkWorkflowStatusContent';
import BulkAssigneeContent from '../BillTriage/BulkAssigneeContent';
import BulkPriorityContent from '../BillTriage/BulkPriorityContent';
import useMarkBillsAsRead from '../BillTriage/useMarkBillsAsRead';
import featureFlags, { FrontendFeature } from '../../../featureFlags';
import { getOrgForTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';

type BillBulkActionsDropdownProps = {
  bills: Bill[];
  // Shown only in the What's New Updated Bills section, where every listed bill is unread.
  showMarkAsRead?: boolean;
  // Called once the bills are marked read. The table clears its selection, so Mark as Read
  // can't be sent again for the same bills while the list refreshes.
  onMarkedAsRead?: () => void;
};

const BillBulkActionsDropdown = (props: BillBulkActionsDropdownProps): ReactElement => {
  const { bills, showMarkAsRead = false, onMarkedAsRead } = props;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const orgId = useSelector(getOrgForTeamMode)?.id;
  const canViewWorkspaceColumns = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.BillTriageWorkspaceColumns,
    orgUser,
  );

  const markBillsAsRead = useMarkBillsAsRead();
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const handleMarkAsRead = (): void => {
    if (!orgId || markingAsRead || !bills.length) return;
    setMarkingAsRead(true);
    void markBillsAsRead({ orgId, billIds: bills.map((bill) => bill.id) })
      .then((marked) => {
        if (marked) onMarkedAsRead?.();
      })
      .finally(() => setMarkingAsRead(false));
  };

  return (
    <NestedDropdown
      buttonClassName="dropdown-button dropdown-toggle bulk-dropdown-button"
      isDisabled={!bills || bills.length < 1}
      menuClassName="menu-item"
      position="right"
      title={
        <>
          <FontAwesomeIcon className="dropdown-icon" icon={PencilIcon} />
          <span className="bulk-dropdown-text">Bulk Actions</span>
          <ArrowDown className="dropdown-arrow" />
        </>
      }
      wrapperClassName="dropdown bulk-actions-dropdown"
    >
      <NestedDropdown.Item>
        <FontAwesomeIcon className="dropdown-icon" icon={TrackingIcon} />
        <span className="dropdown-item-text">Tracking</span>
        <FontAwesomeIcon
          className="dropdown-item-icon right-icon"
          icon={ChevronRightIcon}
        />
        <NestedDropdown.Submenu position="right">
          <NestedDropdown.Item className="submenu-item">
            <BulkTrackingContent bills={bills} />
          </NestedDropdown.Item>
        </NestedDropdown.Submenu>
      </NestedDropdown.Item>
      <NestedDropdown.Item>
        <FontAwesomeIcon className="dropdown-item-icon" icon={TagIcon} />
        <span className="dropdown-item-text">Tags</span>
        <FontAwesomeIcon
          className="dropdown-item-icon right-icon"
          icon={ChevronRightIcon}
        />
        <NestedDropdown.Submenu position="right">
          <NestedDropdown.Item className="submenu-item">
            <BillTagContent billData={bills} />
          </NestedDropdown.Item>
        </NestedDropdown.Submenu>
      </NestedDropdown.Item>
      {canViewWorkspaceColumns && (
        <NestedDropdown.Item>
          <FontAwesomeIcon className="dropdown-item-icon" icon={WorkflowStatusIcon} />
          <span className="dropdown-item-text">Workflow Status</span>
          <FontAwesomeIcon
            className="dropdown-item-icon right-icon"
            icon={ChevronRightIcon}
          />
          <NestedDropdown.Submenu position="right">
            <NestedDropdown.Item className="submenu-item">
              <BulkWorkflowStatusContent bills={bills} />
            </NestedDropdown.Item>
          </NestedDropdown.Submenu>
        </NestedDropdown.Item>
      )}
      {canViewWorkspaceColumns && (
        <NestedDropdown.Item>
          <FontAwesomeIcon className="dropdown-item-icon" icon={AssigneeIcon} />
          <span className="dropdown-item-text">Assignee</span>
          <FontAwesomeIcon
            className="dropdown-item-icon right-icon"
            icon={ChevronRightIcon}
          />
          <NestedDropdown.Submenu position="right">
            <NestedDropdown.Item className="submenu-item">
              <BulkAssigneeContent bills={bills} />
            </NestedDropdown.Item>
          </NestedDropdown.Submenu>
        </NestedDropdown.Item>
      )}
      {canViewWorkspaceColumns && (
        <NestedDropdown.Item>
          <FontAwesomeIcon className="dropdown-item-icon" icon={PriorityIcon} />
          <span className="dropdown-item-text">Priority</span>
          <FontAwesomeIcon
            className="dropdown-item-icon right-icon"
            icon={ChevronRightIcon}
          />
          <NestedDropdown.Submenu position="right">
            <NestedDropdown.Item className="submenu-item">
              <BulkPriorityContent bills={bills} />
            </NestedDropdown.Item>
          </NestedDropdown.Submenu>
        </NestedDropdown.Item>
      )}
      {showMarkAsRead && (
        <NestedDropdown.Item
          isDisabled={!orgId || markingAsRead || !bills.length}
          onClick={handleMarkAsRead}
        >
          <FontAwesomeIcon className="dropdown-item-icon" icon={MarkAsReadIcon} />
          <span className="dropdown-item-text">Mark as Read</span>
        </NestedDropdown.Item>
      )}
    </NestedDropdown>
  );
};

export default BillBulkActionsDropdown;
